import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ScrollText, Filter, Trash2, RefreshCw, Download, AlertCircle, AlertTriangle, Info, Bug, Loader2, Pause, Play } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const LEVEL_ICONS = {
  debug: Bug,
  info: Info,
  warn: AlertTriangle,
  error: AlertCircle
};

const LEVEL_COLORS = {
  debug: 'text-gray-400 bg-gray-500/10',
  info: 'text-blue-400 bg-blue-500/10',
  warn: 'text-yellow-400 bg-yellow-500/10',
  error: 'text-red-400 bg-red-500/10'
};

function LogRow({ log }) {
  const Icon = LEVEL_ICONS[log.level] || Info;
  const colorClass = LEVEL_COLORS[log.level] || LEVEL_COLORS.info;
  const time = new Date(log.timestamp).toLocaleTimeString('es');
  
  return (
    <div className={`flex items-start gap-3 p-2 rounded-lg ${colorClass} border border-transparent hover:border-slate-600`}>
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <span className="font-mono">{time}</span>
          <span className="px-1.5 py-0.5 rounded text-xs bg-slate-700/50">{log.source}</span>
        </div>
        <p className="text-sm text-white font-mono break-all">{log.message}</p>
        {log.metadata && Object.keys(log.metadata).length > 0 && (
          <pre className="mt-1 text-xs text-gray-400 overflow-x-auto">{JSON.stringify(log.metadata, null, 2)}</pre>
        )}
      </div>
    </div>
  );
}

function LogsPanel({ projectId, onClose }) {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ levels: ['info', 'warn', 'error'], search: '' });
  const [streaming, setStreaming] = useState(false);
  const [paused, setPaused] = useState(false);
  const wsRef = useRef(null);
  const logsEndRef = useRef(null);

  const fetchLogs = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    const token = localStorage.getItem('cloudtexa_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/logs/${projectId}?limit=200`, { headers }),
        axios.get(`${API}/api/logs/${projectId}/stats`, { headers })
      ]);
      setLogs(logsRes.data.logs || []);
      setStats(statsRes.data);
    } catch (e) { 
      if (e.response?.status !== 404) {
        toast.error('Error cargando logs');
      }
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const connectWebSocket = useCallback(() => {
    if (wsRef.current) return;
    
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    const token = localStorage.getItem('cloudtexa_token');
    
    try {
      wsRef.current = new WebSocket(`${wsUrl}/api/logs/${projectId}/stream`);
      
      wsRef.current.onopen = () => {
        setStreaming(true);
        toast.success('Streaming de logs activo');
      };
      
      wsRef.current.onmessage = (event) => {
        if (paused) return;
        const data = JSON.parse(event.data);
        if (data.type !== 'heartbeat') {
          setLogs(prev => [...prev.slice(-199), data]);
          logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      };
      
      wsRef.current.onclose = () => {
        setStreaming(false);
        wsRef.current = null;
      };
      
      wsRef.current.onerror = () => {
        toast.error('Error de conexión WebSocket');
        setStreaming(false);
        wsRef.current = null;
      };
    } catch (e) {
      console.error('WebSocket error:', e);
    }
  }, [projectId, paused]);

  const disconnectWebSocket = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
      setStreaming(false);
    }
  };

  useEffect(() => {
    return () => disconnectWebSocket();
  }, []);

  const clearLogs = async () => {
    if (!window.confirm('¿Eliminar todos los logs?')) return;
    try {
      const token = localStorage.getItem('cloudtexa_token');
      await axios.delete(`${API}/api/logs/${projectId}`, { headers: { Authorization: `Bearer ${token}` } });
      setLogs([]);
      toast.success('Logs eliminados');
      fetchLogs();
    } catch (e) { toast.error('Error'); }
  };

  const exportLogs = () => {
    const data = JSON.stringify(logs, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs_${projectId}_${Date.now()}.json`;
    a.click();
    toast.success('Logs exportados');
  };

  const toggleLevel = (level) => {
    setFilters(prev => ({
      ...prev,
      levels: prev.levels.includes(level) 
        ? prev.levels.filter(l => l !== level)
        : [...prev.levels, level]
    }));
  };

  const filteredLogs = logs.filter(log => {
    if (!filters.levels.includes(log.level)) return false;
    if (filters.search && !log.message.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-orange-400" /></div>;

  return (
    <div className="h-full flex flex-col bg-slate-900" data-testid="logs-panel">
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ScrollText className="w-5 h-5 text-orange-400" />
            <h2 className="font-medium text-white">Logs</h2>
            {streaming && <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-green-500/20 text-green-400"><span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />Live</span>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="flex gap-3 mt-3">
            <div className="flex items-center gap-1.5 text-xs">
              <span className="w-2 h-2 rounded-full bg-gray-400" />
              <span className="text-gray-400">Debug: {stats.by_level?.debug || 0}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="w-2 h-2 rounded-full bg-blue-400" />
              <span className="text-gray-400">Info: {stats.by_level?.info || 0}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="w-2 h-2 rounded-full bg-yellow-400" />
              <span className="text-gray-400">Warn: {stats.by_level?.warn || 0}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="w-2 h-2 rounded-full bg-red-400" />
              <span className="text-gray-400">Error: {stats.by_level?.error || 0}</span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 mt-3">
          <div className="flex items-center gap-1 p-1 bg-slate-800 rounded-lg">
            {['debug', 'info', 'warn', 'error'].map(level => {
              const Icon = LEVEL_ICONS[level];
              const active = filters.levels.includes(level);
              return (
                <button key={level} onClick={() => toggleLevel(level)} className={`p-1.5 rounded ${active ? LEVEL_COLORS[level] : 'text-gray-600'}`} title={level}>
                  <Icon className="w-4 h-4" />
                </button>
              );
            })}
          </div>
          <input type="text" value={filters.search} onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))} placeholder="Buscar..." className="flex-1 px-3 py-1.5 text-sm rounded-lg bg-slate-800 text-white outline-none" />
          <button onClick={fetchLogs} className="p-1.5 text-gray-400 hover:bg-slate-800 rounded" title="Refrescar"><RefreshCw className="w-4 h-4" /></button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-3">
          {!streaming ? (
            <button onClick={connectWebSocket} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30">
              <Play className="w-4 h-4" />Stream
            </button>
          ) : (
            <button onClick={() => setPaused(!paused)} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg ${paused ? 'bg-yellow-500/20 text-yellow-400' : 'bg-orange-500/20 text-orange-400'}`}>
              {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              {paused ? 'Continuar' : 'Pausar'}
            </button>
          )}
          {streaming && (
            <button onClick={disconnectWebSocket} className="px-3 py-1.5 text-sm text-red-400 hover:bg-red-500/20 rounded-lg">Desconectar</button>
          )}
          <button onClick={exportLogs} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-400 hover:bg-slate-800 rounded-lg"><Download className="w-4 h-4" />Exportar</button>
          <button onClick={clearLogs} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-400 hover:bg-red-500/20 rounded-lg"><Trash2 className="w-4 h-4" />Limpiar</button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {filteredLogs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ScrollText className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>Sin logs</p>
            <p className="text-xs mt-1">Los logs aparecerán cuando tu app esté en ejecución</p>
          </div>
        ) : (
          <>
            {filteredLogs.map((log, i) => <LogRow key={log.id || i} log={log} />)}
            <div ref={logsEndRef} />
          </>
        )}
      </div>
    </div>
  );
}

export default LogsPanel;
